import type { Editor } from '@tiptap/react'
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Italic,
  Link as LinkIcon,
  Strikethrough,
  Underline as UnderlineIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { LinkPopper } from './LinkPopper'

interface ButtonProps {
  active: boolean
  onClick: () => void
  children: ReactNode
}

const ToolbarButton = ({ active, onClick, children }: ButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'p-1.5 rounded-lg transition-colors',
      active
        ? 'bg-accent text-accent-foreground'
        : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
    )}
  >
    {children}
  </button>
)

interface Props {
  editor: Editor
  linkOpenRequest: number
}

export const BubbleToolbar = ({ editor, linkOpenRequest }: Props) => {
  const [mode, setMode] = useState<'default' | 'link'>('default')

  useEffect(() => {
    if (linkOpenRequest > 0) setMode('link')
  }, [linkOpenRequest])

  if (mode === 'link') {
    return <LinkPopper editor={editor} onClose={() => setMode('default')} />
  }

  return (
    <div className="flex items-center gap-0.5 bg-popover border border-border rounded-xl shadow-lg p-1">
      <ToolbarButton
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="w-3.5 h-3.5" />
      </ToolbarButton>
      <div className="w-px h-5 bg-border mx-0.5" />
      <ToolbarButton
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <Code className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('highlight')}
        onClick={() => editor.chain().focus().toggleHighlight().run()}
      >
        <Highlighter className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton active={editor.isActive('link')} onClick={() => setMode('link')}>
        <LinkIcon className="w-3.5 h-3.5" />
      </ToolbarButton>
      <div className="w-px h-5 bg-border mx-0.5" />
      <ToolbarButton
        active={editor.isActive('heading', { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="w-3.5 h-3.5" />
      </ToolbarButton>
    </div>
  )
}
